'use client' // This ensures the component runs in the client-side context

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useEffect } from 'react'
import { client } from '../../client/client.gen'
import { isAiClaimsPortalPath } from '../config/aiclaims-routes'
import {
  defaultBrandFromHostname,
} from '../lib/brandFromHost'
import { isWysehubPublicEntryPath } from '../lib/hostRouting'
import { isAiClaimsPortalHostname } from '../lib/managedHosts'
import { sanitizeInternalRedirect } from '../utils/safeRedirect'
import {
  csrfHeaders,
  ensureCsrfCookie,
  isCsrfFailureDetail,
  readBrowserCookie,
} from '../utils/csrf'

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _csrfRetry?: boolean
}

let refreshInFlight: Promise<boolean> | null = null

async function attemptSilentRefresh(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight
  }

  refreshInFlight = (async () => {
    try {
      await ensureCsrfCookie()
      const response = await fetch('/api/v1/login/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...csrfHeaders(),
        },
      })
      return response.ok
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

function redirectToLogin() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('effectivePartnerType')

  const currentPath = window.location.pathname
  if (
    currentPath === '/' ||
    currentPath.includes('login') ||
    isWysehubPublicEntryPath(currentPath)
  ) {
    return
  }

  console.log('🚪 Redirecting to login due to authentication failure')
  const safePath = sanitizeInternalRedirect(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    window.location.origin,
  )
  if (safePath) {
    localStorage.setItem('loginRedirectUrl', safePath)
  }
  window.location.href = '/?login=true'
}

function csrfDetailFromError(error: AxiosError): unknown {
  const data = error.response?.data
  if (data && typeof data === 'object' && 'detail' in data) {
    return (data as { detail: unknown }).detail
  }
  return undefined
}

const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    axios.defaults.withCredentials = true

    client.setConfig({
      axios,
      baseURL: '',
      throwOnError: true,
      withCredentials: true,
    })

    // Sessions from before CSRF cookies can mutate without a token; bootstrap one.
    void ensureCsrfCookie()

    const requestInterceptor = axios.interceptors.request.use((config) => {
      const csrf = readBrowserCookie('csrf_token')
      if (csrf) {
        config.headers.set('X-CSRF-Token', csrf)
      }
      if (typeof window !== 'undefined') {
        const brand = defaultBrandFromHostname(
          window.location.hostname,
          process.env.NEXT_PUBLIC_AICLAIMS_HOST,
          process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST,
        )
        config.headers.set('X-Default-Brand', brand)
        const csrfHeader = csrfHeaders()
        for (const [key, value] of Object.entries(csrfHeader)) {
          if (value) {
            config.headers.set(key, value)
          }
        }
      }
      return config
    })

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const status = error.response?.status
        const originalConfig = error.config as RetriableRequestConfig | undefined
        const requestUrl = String(originalConfig?.url ?? '')

        if (status === 401) {
          const isRefreshCall = requestUrl.includes('/login/refresh')
          if (!isRefreshCall && originalConfig && !originalConfig._retry) {
            originalConfig._retry = true
            const refreshed = await attemptSilentRefresh()
            if (refreshed) {
              return axios.request(originalConfig)
            }
          }

          console.log(
            `🔐 Authentication error detected (${status}): Session expired`,
          )
          redirectToLogin()
        }

        if (status === 403) {
          const csrfFailed = isCsrfFailureDetail(csrfDetailFromError(error))

          if (
            csrfFailed &&
            originalConfig &&
            !originalConfig._csrfRetry &&
            !requestUrl.includes('/csrf-token')
          ) {
            originalConfig._csrfRetry = true
            const token = await ensureCsrfCookie({ force: true })
            if (token) {
              originalConfig.headers.set('X-CSRF-Token', token)
              return axios.request(originalConfig)
            }
          }

          // CSRF failures are not authorization failures — surface to the caller.
          if (csrfFailed) {
            return Promise.reject(error)
          }

          console.log(
            `🚫 Forbidden error detected (${status}): Access denied`,
          )

          if (
            requestUrl.includes('/users/me/active-brand') ||
            requestUrl.includes('/users/me/current-partner')
          ) {
            return Promise.reject(error)
          }

          const hostname =
            typeof window !== 'undefined'
              ? window.location.hostname
              : ''
          const isAiClaimsPortalHost = isAiClaimsPortalHostname(hostname)
          const isAiClaimsPath = isAiClaimsPortalPath(window.location.pathname)

          if (isAiClaimsPortalHost && isAiClaimsPath) {
            if (window.location.pathname !== '/unauthorised') {
              window.location.href = '/unauthorised'
            }
          } else if (window.location.pathname !== '/unauthorised') {
            window.location.href = '/unauthorised'
          }
        }

        return Promise.reject(error)
      },
    )

    return () => {
      axios.interceptors.request.eject(requestInterceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, [])

  return <>{children}</>
}

export default GlobalProvider

export { attemptSilentRefresh }
